import { Component, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { CustomColDef } from '@core/types';
import { RegistryService } from '@core/utils';
import { AgGridYaloComponent } from '@shared/ag-components';
import { Subject } from 'rxjs';
import { filter, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { DrawersService } from '../drawers.service';
import { DynamicColumnFilters, FilterOption, RangeNumber } from '@pages/dashboard/informes/utils/filter-models';

@Component({
  selector: 'app-columns-drawer',
  templateUrl: 'columns-drawer.component.html',
  styleUrls: ['columns-drawer.component.scss'],
})
export class ColumnsDrawerComponent implements OnInit, OnDestroy {
  columnDefs: CustomColDef[] = [];
    private readonly FILTER_TYPES = {
    TEXT: 'agTextColumnFilter',
    NUMBER: 'agNumberColumnFilter'
  };
  filters: DynamicColumnFilters = {};

  @Output() filtersChanged = new EventEmitter<DynamicColumnFilters>();

  private name = '';
  private unsubscribeAll: Subject<void> = new Subject<void>();

  constructor(
    private drawersService: DrawersService,
    private registryService: RegistryService
  ) {}

  //#region LIFECYCLE HOOKS
  ngOnInit(): void {
    this.drawersService.columnDefs$
      .pipe(
        filter((data) => !!data),
        takeUntil(this.unsubscribeAll)
      )
      .subscribe(({ name, columnDefs }) => {
        this.name = name;
        this.filters = {};
        this.columnDefs = columnDefs
          .filter(col => !col.hidden)
          .map(col => {
            if (col.filterOptions && col.filterOptions.length > 0) {
              col.filterOptions.forEach(opt => {
                opt.selected = true;
              });
              this.updateFilter(col, col.filterOptions, 'set');
            }
            return { ...col, visible: true };
          });
      });
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }
  //#endregion

  //#region GETTERS
  private get getAgGrid(): any {
    return this.registryService.getComponent<AgGridYaloComponent<any>>(this.name)?.agGrid;
  }
  //#endregion

  //#region PUBLIC METHODS
  isTextFilter(column: CustomColDef): boolean {
    return column.filter === this.FILTER_TYPES.TEXT && !column.filterOptions;
  }

  isNumberFilter(column: CustomColDef): boolean {
    return column.filter === this.FILTER_TYPES.NUMBER && !column.filterOptions;
  }

  isSetFilter(column: CustomColDef): boolean {
    return  !!column.filterOptions;
  }

  onTextFilterChange(column: CustomColDef, input: any): void {
    const value = input.target.value.trim();
    this.updateFilter(column, value, 'text');
  }

  onRangeFilterChange(column: CustomColDef, type: 'from' | 'to', input: any): void {
    const value = input.target.value;
    const currentFilter = this.filters[column.field]?.value as RangeNumber || {from: null, to: null};
    const rangeValue: RangeNumber = {
      from: type === 'from' ? (value ? Number(value) : null) : currentFilter?.from,
      to: type === 'to' ? (value ? Number(value) : null) : currentFilter.to
    };

    if (rangeValue.from || rangeValue.to) {
      this.updateFilter(column, rangeValue, 'range');
    } else {
      this.updateFilter(column, null, 'range');
    }
  }

  onSelectFilterChange(column: CustomColDef, option: FilterOption): void {
    if (!column.field || !column.filterOptions) return;
    option.selected = !option.selected;
    const selectedOptions = column.filterOptions.filter(opt => opt.selected);


    this.updateFilter(column, selectedOptions.length ? selectedOptions : null, 'set');
  }

  onSelectAllChange(column: CustomColDef, selected: boolean): void {
    if (!column.field || !column.filterOptions) return;

    column.filterOptions.forEach(option => (option.selected = selected));

    const selectedOptions = selected
      ? column.filterOptions
      : null;


    this.updateFilter(column, selectedOptions, 'set');
  }

  AllSelected(column: CustomColDef): boolean {
    return column.filterOptions?.every(option => option.selected) || false;
  }

  clearFilters(): void {
    this.filters = {};
    const agGrid = this.getAgGrid;

    if (agGrid) {
      agGrid.api.setFilterModel(null);

      const textInputs = document.querySelectorAll('.filter-input') as NodeListOf<HTMLInputElement>;
      const rangeInputs = document.querySelectorAll('.range-inputs input') as NodeListOf<HTMLInputElement>;
      textInputs.forEach(input => input.value = '');
      rangeInputs.forEach(input => input.value = '');

      this.columnDefs = this.columnDefs.map(col => {
        if (col.filterOptions) {
          col.filterOptions.forEach(option => option.selected = true);
          this.filters[col.field] = {
            type: 'set',
            value: col.filterOptions
          };
        }
        if (this.name.startsWith('ag')) {
          agGrid.columnApi.setColumnVisible(col.field, true);
        }
        return { ...col, visible: true, expanded: false };
      });

      this.filtersChanged.emit(this.filters);
      this.drawersService.setColumnFilters(this.filters);
      this.drawersService.setColumnsVisible(this.columnDefs);
    }
  }
  //#endregion

  //#region PRIVATE METHODS
  private updateFilter(column: CustomColDef, value: any, filterType: 'text' | 'range' | 'set'): void {
    if (!column.field) return;

    if (value) {
      this.filters[column.field] = { type: filterType, value };
    } else {
      delete this.filters[column.field];
    }

    const filterModel = this.createFilterModel(filterType, value);
    console.log('filterModel', filterModel);
    this.applyFilter(column, filterModel);
    this.filtersChanged.emit(this.filters);
    this.drawersService.setColumnFilters(this.filters);
  }

  private createFilterModel(filterType: string, value: any): any {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return null;
    }
    if (!value) {
      return null;
    }

    switch (filterType) {
      case 'text':
        return {
          filterType: 'text',
          type: 'contains',
          filter: value
        };
      case 'range':
        return {
          filterType: 'number',
          // type: 'inRange',
          // filter: value.from,
          // filterTo: value.to,
          // inRangeInclusive: true
          operator: 'AND',
          condition1: {
            filterType: 'number',
            type: 'greaterThanOrEqual',
            filter: value.from
          },
          condition2: {
            filterType: 'number',
            type: 'lessThanOrEqual',
            filter: value.to
          }
        };
      case 'set':
        return this.buildCheckboxFilter(value);
      default:
        return null;
    }
  }

  private buildCheckboxFilter(selectedOptions: FilterOption[]): any {
    if (!selectedOptions || selectedOptions.length === 0) {
      return null;
    }
    const selectedLabels = new Set(selectedOptions.map(opt => opt.label.toString().trim().toLowerCase()));

  return {
    filterType: 'text',
    type: 'custom',
    filter: selectedLabels,
    filterFunction: (filterValue: Set<string>, cellValue: any) => {
      if (!cellValue) {
        return false;
      }

      console.log('Filter Value (Set):', Array.from(filterValue), 'Cell Value:');

      if (filterValue.size === 0) return false;

      const result = filterValue.has(cellValue);
      console.log('Filter result:', result);
      return result;
    }
  };
  }

  private buildOrConditions(values: FilterOption[]): any {
    if (!values || values.length === 0) {
      return null;
    }

    if (values.length === 1) {
      return {
        filterType: 'text',
        type: 'equals',
        filter: values[0].label
      };
    }

    return {
      filterType: 'text',
      type: 'custom',
      filter: values,
      filterFunction: (filterValue: FilterOption[], cellValue: any) => {
        if (!cellValue) return false;

        const valueToCompare = typeof cellValue === 'object' && cellValue.label
          ? cellValue.label.toString().toLowerCase()
          : cellValue.toString().toLowerCase();

        return filterValue.some(v => v.label.toString().toLowerCase() === valueToCompare);
      }
  };
  }

  private applyFilter(column: CustomColDef, filterModel: any): void {
    const agGrid = this.getAgGrid;
    if (!agGrid) return;

    const instance = agGrid.api.getFilterInstance(column.field);
    if (!instance) return;
    console.log('instance', instance);

    instance.setModel(filterModel);
    agGrid.api.onFilterChanged();
  }

  private updateAgGridVisibility(field: string, visible: boolean): void {
    const agGrid = this.getAgGrid;
    if (!agGrid) return;

    const filteredColumns = agGrid.columnApi
      .getColumns()
      .filter(col => col.getColDef().field === field);

    if (filteredColumns.length > 1) {
      agGrid.columnApi.setColumnsVisible(filteredColumns, visible);
    } else {
      agGrid.columnApi.setColumnVisible(field, visible);
    }
  }
  //#endregion

  //#region UI METHODS
  closeDrawer(): void {
    const drawer = this.drawersService.getDrawer('columnsDrawer');
    if (drawer) drawer.close();
  }

  onVisibleChanged(column: CustomColDef): void {
    if (this.name.startsWith('ag')) {
      this.updateAgGridVisibility(column.field, column.visible);
    }

    const columnsVisible = this.columnDefs.filter(c => c.visible);
    this.drawersService.setColumnsVisible(columnsVisible);
  }

  trackByFn(_index: number, item: CustomColDef): string {
    return item.field;
  }
  //#endregion
}
